// useBuilderInput - 新しい Builder 入力システム
// useScene.ts と同じ機能を提供、src/engine ベース

import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { nanoid } from "nanoid";
import { Mesh } from "@babylonjs/core/Meshes";
import { Scene } from "@babylonjs/core/scene";
import { Observer } from "@babylonjs/core/Misc/observable";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Button, AdvancedDynamicTexture } from "@babylonjs/gui";
import { PickingInfo } from "@babylonjs/core/Collisions/pickingInfo";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { IPointerEvent } from "@babylonjs/core/Events/deviceInputEvents";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { PointerInfo, PointerEventTypes } from "@babylonjs/core/Events/pointerEvents";
import { RootState } from "app/store";
import Constants from "common/constant";
import { usePlaySound } from "common/utils";
import { AssetModelType } from "common/model";
import { actions } from "features/builder/slice";
import { useActionCommand } from "features/builder/hook";
import { ActionCommandType } from "features/builder/types";
import { createPreviewMesh } from "features/builder/canvas/scene/loader";
import {
  createGround,
  createOuterNode,
  canPlaceOnGround,
  createBottomGround,
} from "features/builder/canvas/scene/util";

/** 置けないときの案内を出す間隔（連打で何度も出さない） */
const NOTIFY_INTERVAL = 600;

export const useBuilderInput = (scene: Scene | null) => {
  const play = usePlaySound();
  const dispatch = useDispatch();
  const { handleAction } = useActionCommand();
  const actionMenu = useSelector((state: RootState) => state.builder.actionMenu);
  const selectedAssetModel = useSelector((state: RootState) => state.builder.selectedAssetModel);

  // 回転ターゲット（選択中のアセット）
  const [target, _setTarget] = useState<Mesh | null>(null);
  const targetRef = useRef(target);
  const setTarget = (t: Mesh | null) => {
    targetRef.current = t;
    _setTarget(t);
    // Redux にも同期
    dispatch(actions.selectPlacedModel(t?.id ?? null));
  };

  // ターゲットクリア（3Dキャンバスをクリックしたとき）
  useEffect(() => {
    if (!scene) return;
    const canvas = scene.getEngine().getRenderingCanvas();
    if (!canvas) return;

    const handleClearTarget = () => {
      if (target) {
        target.getChildMeshes().forEach((subMesh) => {
          if (subMesh.material) subMesh.material.alpha = 1;
        });
        setTarget(null);
      }
    };
    canvas.addEventListener("pointerdown", handleClearTarget);
    return () => {
      canvas.removeEventListener("pointerdown", handleClearTarget);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, scene]);

  // 回転ボタン表示
  useEffect(() => {
    let rotateBtn: Button;
    if (target) {
      const turnMesh = () => {
        const lastForward = target.forward;
        target.addRotation(0, Math.PI / 2, 0);
        dispatch(
          actions.updateMapModel({
            id: target.id,
            changes: {
              direction: {
                x: Number(Math.round(lastForward.z)),
                y: 0,
                z: Number(-Math.round(lastForward.x)),
              },
            },
          }),
        );
      };

      const boundingInfo = target.getBoundingInfo();
      const advancedTexture = AdvancedDynamicTexture.CreateFullscreenUI("UI");
      advancedTexture.layer.layerMask = 2;

      rotateBtn = Button.CreateImageOnlyButton(
        "rotateBtn",
        `${Constants.assetHost}/assets/images/icon_reset_rotation.png`,
      );
      rotateBtn.width = "80px";
      rotateBtn.height = "80px";
      rotateBtn.thickness = 0;
      rotateBtn.onPointerDownObservable.add((_, eventState) => {
        eventState.userInfo.event.stopPropagation();
        turnMesh();
      });
      advancedTexture.addControl(rotateBtn);
      rotateBtn.linkWithMesh(target);
      rotateBtn.linkOffsetY = -(boundingInfo.boundingBox.maximumWorld.y + 150);
    }
    return () => {
      if (rotateBtn) {
        rotateBtn.dispose();
      }
    };
  }, [target, actionMenu, dispatch]);

  // メインのポインターイベント処理
  useEffect(() => {
    let clicked = false;
    let pointerUpFlag = false;
    let pointerDownFlag = false;
    // 進行中のポインター。マルチタッチで2本目の指に状態を奪われないよう1本だけを追う
    let activePointerId: number | null = null;

    let deleteMesh: Mesh | null = null;
    let selectedMesh: Mesh | null = null;
    let previewedMesh: Mesh | null = null;
    let observable: Observer<PointerInfo> | null = null;
    let handlePointerCancel: ((event: PointerEvent) => void) | null = null;
    let lastNotifiedAt = 0;
    // 吹き出しを出す場所（最後に指を置いた画面上の位置）
    let lastPointer = { x: 0, y: 0 };

    /**
     * タップした所で、アセットを置ける「上の面」を返す。
     * ブロックの上面（せまい菱形）を正確に突かないと置けないと、子どもには難しいので、
     * ブロックの横や斜めを触ったときは、そのブロックの上の面を使う。
     * 面（asset_ground_layer_x_z_h）は列ごとに1枚だけで、常に一番上にある。
     */
    const placementGround = (type?: AssetModelType) => {
      const hitAsset = scene!.pick(scene!.pointerX, scene!.pointerY, (mesh) =>
        mesh.name.includes("asset"),
      );
      const hitGround = scene!.pick(scene!.pointerX, scene!.pointerY, (mesh) =>
        mesh.name.includes("ground"),
      );
      const layerOf = (name?: string) => name?.split("_").pop();
      if (
        hitAsset.hit &&
        hitGround.hit &&
        layerOf(hitGround.pickedMesh?.name) === layerOf(hitAsset.pickedMesh?.name) &&
        canPlaceOnGround(hitGround.pickedMesh, type)
      ) {
        return hitGround.pickedMesh;
      }
      const assetName = hitAsset.pickedMesh?.name;
      if (assetName?.includes("model")) {
        const top = scene!.getMeshByName(assetName.replace("model", "ground"));
        if (top && canPlaceOnGround(top, type)) return top;
      }
      return null;
    };

    // 置けないところをタップしたときに、どうすればいいかを伝える。
    // 黙って何も起きないと、子どもは自分の操作が効いていないことに気づけない。
    // 指を置いた場所に出す（画面いちばん上のトーストは見てもらえない）
    const notifyCannotPlace = () => {
      const now = Date.now();
      if (now - lastNotifiedAt < NOTIFY_INTERVAL) return;
      lastNotifiedAt = now;
      const onTile =
        selectedAssetModel?.type === AssetModelType.GROUND ||
        selectedAssetModel?.type === AssetModelType.RIVER;
      dispatch(
        actions.showPlaceHint({
          messageKey: onTile ? "MSG_BUILDER_PLACE_ON_TILE" : "MSG_BUILDER_PLACE_ON_GROUND",
          x: lastPointer.x,
          y: lastPointer.y,
        }),
      );
    };

    const canvas = scene?.getEngine().getRenderingCanvas() ?? null;

    if (scene) {
      const camera = scene.getCameraByName("camera") as ArcRotateCamera;

      // カメラを detach すると Babylon が canvas の touch-action を戻すため、
      // 操作中にページ側のスクロール/ピンチが割り込まないよう明示的に止める
      const lockCanvasGesture = () => {
        if (canvas) canvas.style.touchAction = "none";
      };

      // 操作を中断し、掴んでいたアセットを元の位置に戻して後始末する
      const abortInteraction = () => {
        if (previewedMesh) {
          (previewedMesh.parent as TransformNode).dispose();
          previewedMesh = null;
        }
        if (selectedMesh) {
          const outer = selectedMesh.parent as TransformNode;
          const boundingInfo = selectedMesh.getBoundingInfo();
          const position = new Vector3(
            selectedMesh.metadata.position.x,
            selectedMesh.metadata.position.y,
            selectedMesh.metadata.position.z,
          );
          outer.position = position;
          outer.getChildMeshes().forEach((subMesh) => {
            if (subMesh.material && !selectedMesh!.metadata?.rotation) {
              subMesh.material.alpha = 1;
            }
          });
          createGround(
            scene,
            boundingInfo.boundingBox,
            selectedMesh.metadata.type,
            position,
            selectedMesh.metadata.bottomLayer,
          );
          selectedMesh = null;
        }
        deleteMesh = null;
        clicked = false;
        activePointerId = null;
        scene.getMaterialByName("ground")!.alpha = 0;
        scene.getMaterialByName("ground_layer")!.alpha = 0;
        camera?.attachControl(scene.getEngine().getRenderingCanvas()!, true);
      };

      // ポインターダウン処理
      const pointerDown = async (pickInfo: PickingInfo) => {
        if (pointerDownFlag || pointerUpFlag) return;
        clicked = true;
        pointerDownFlag = true;

        if (pickInfo.pickedMesh) {
          if (selectedAssetModel) {
            // ADD モード：アセット配置
            camera?.detachControl();
            lockCanvasGesture();

            const groundMesh = placementGround(selectedAssetModel.type);

            if (!previewedMesh && groundMesh) {
              scene.getMaterialByName("ground_layer")!.alpha = 0.3;
              if (
                selectedAssetModel.type === AssetModelType.RIVER ||
                selectedAssetModel.type === AssetModelType.GROUND
              ) {
                scene.getMaterialByName("ground")!.alpha = 0.3;
              }

              const mesh = await createPreviewMesh(
                scene,
                selectedAssetModel.filename,
                selectedAssetModel.offset,
              );
              if (clicked) {
                mesh.getChildMeshes().forEach((subMesh) => {
                  if (subMesh.material) subMesh.material.alpha = 0.8;
                  if (subMesh.name.indexOf("Cube") !== -1) {
                    subMesh.isVisible = false;
                  }
                });
                const outer = createOuterNode(scene, groundMesh.position);
                if (selectedAssetModel.type === AssetModelType.CHARACTER) {
                  outer.scaling = new Vector3(
                    selectedAssetModel.scaling.x,
                    selectedAssetModel.scaling.y,
                    selectedAssetModel.scaling.z,
                  );
                  scene.animationGroups.forEach((group) => {
                    group.stop();
                  });
                }
                mesh.metadata = {
                  type: selectedAssetModel.type,
                  rotation: selectedAssetModel.rotation,
                };
                mesh.parent = outer;
                previewedMesh = mesh;
              } else {
                mesh.dispose();
                previewedMesh = null;
                scene.getMaterialByName("ground")!.alpha = 0;
                scene.getMaterialByName("ground_layer")!.alpha = 0;
              }
            } else if (!previewedMesh) {
              play();
              notifyCannotPlace();
            }
          } else {
            // SELECT/ERASE モード
            if (!actionMenu.eraser) {
              dispatch(
                actions.updateActionMenu({
                  key: "tap",
                  value: true,
                  exclusive: true,
                }),
              );
            }

            // ground以外のすべてのアセットメッシュを選択可能に
            const hitAssetMesh = scene.pick(scene.pointerX, scene.pointerY, (mesh) => {
              // outer の子要素であればアセットメッシュ
              let parent = mesh.parent;
              while (parent) {
                if (parent.name === "outer") return true;
                parent = parent.parent;
              }
              return false;
            });
            if (hitAssetMesh.pickedMesh) {
              let hitMesh = hitAssetMesh.pickedMesh as Mesh;
              while (hitMesh.parent !== null && hitMesh.parent.name !== "outer") {
                hitMesh = hitMesh.parent as Mesh;
              }
              const ground = scene.getMeshByName(hitMesh.name.replace("model", "ground"));
              if (hitMesh.metadata?.type !== "ground" || ground) {
                camera?.detachControl();
                lockCanvasGesture();
                if (targetRef.current) {
                  targetRef.current.getChildMeshes().forEach((subMesh) => {
                    if (subMesh.material) subMesh.material.alpha = 1;
                  });
                  setTarget(null);
                }
                hitMesh.getChildMeshes().forEach((subMesh) => {
                  if (subMesh.material) subMesh.material.alpha = 0.8;
                });
                if (actionMenu.eraser) {
                  deleteMesh = hitMesh;
                } else {
                  // 移動モード
                  scene.getMaterialByName("ground_layer")!.alpha = 0.3;
                  if (
                    hitMesh.metadata?.type === AssetModelType.RIVER ||
                    hitMesh.metadata?.type === AssetModelType.GROUND
                  ) {
                    scene.getMaterialByName("ground")!.alpha = 0.3;
                  }

                  const outer = hitMesh.parent as TransformNode;
                  const boundingInfo = hitMesh.getBoundingInfo();
                  createBottomGround(scene, boundingInfo.boundingBox, outer.position);
                  selectedMesh = hitMesh;
                }
              }
            }
          }
        } else if (selectedAssetModel) {
          // ブロックを持ったまま外した。ここで落とすと、少しずれただけで
          // もう一度えらび直しになる。持ったままにして、どこに置けるかだけ伝える
          play();
          notifyCannotPlace();
        } else {
          // 何もない場所をクリック
          play();
          dispatch(
            actions.updateActionMenu({
              key: "tap",
              value: true,
              exclusive: true,
            }),
          );
          dispatch(actions.selectedAssetMesh(null));
        }

        pointerDownFlag = false;
      };

      // ポインター移動処理
      const pointerMove = async () => {
        if (!clicked || pointerDownFlag || pointerUpFlag) return;
        if ((selectedAssetModel && previewedMesh) || selectedMesh) {
          const hitGroundMesh = scene.pick(scene.pointerX, scene.pointerY, (mesh) =>
            mesh.name.includes("ground"),
          );

          if (hitGroundMesh.pickedPoint) {
            if (
              previewedMesh &&
              canPlaceOnGround(hitGroundMesh.pickedMesh, selectedAssetModel?.type)
            ) {
              (previewedMesh.parent as TransformNode).position =
                hitGroundMesh.pickedMesh!.position.clone();
            }
            if (
              selectedMesh &&
              canPlaceOnGround(hitGroundMesh.pickedMesh, selectedMesh.metadata?.type)
            ) {
              (selectedMesh.parent as TransformNode).position =
                hitGroundMesh.pickedMesh!.position.clone();
            }
          }
        }
      };

      // ポインターアップ処理
      const pointerUp = async (pickInfo: PickingInfo) => {
        clicked = false;

        if (pointerDownFlag || pointerUpFlag) {
          // 処理が重なったときも掴んだままにせず、必ず元に戻して後始末する
          abortInteraction();
          return;
        }

        pointerUpFlag = true;
        scene.getMaterialByName("ground")!.alpha = 0;
        scene.getMaterialByName("ground_layer")!.alpha = 0;

        if (selectedAssetModel && pickInfo.pickedMesh && previewedMesh) {
          // 新しいアセットを配置
          play();
          const animationGroups = [...scene.animationGroups];
          animationGroups.forEach((group) => {
            group.stop();
            group.dispose();
          });
          scene.animationGroups.length = 0;

          const groundMesh = placementGround(selectedAssetModel.type);
          const outer = previewedMesh.parent as TransformNode;
          if (
            groundMesh &&
            groundMesh.position.x === outer.position.x &&
            groundMesh.position.z === outer.position.z
          ) {
            const meshId = nanoid();
            const boundingInfo = previewedMesh.getBoundingInfo();
            const name = `asset_model_layer_${groundMesh.position.x}_${groundMesh.position.z}_${Math.round(boundingInfo.boundingBox.maximumWorld.y)}`;
            outer.getChildMeshes().forEach((subMesh) => {
              if (subMesh.material) {
                subMesh.material.alpha = 1;
              }
              if (subMesh.name !== "outer") {
                subMesh.id = meshId;
                subMesh.name = name;
              }
            });
            previewedMesh.metadata["position"] = {
              x: groundMesh.position.x,
              y: groundMesh.position.y,
              z: groundMesh.position.z,
            };
            previewedMesh.metadata["bottomLayer"] = groundMesh.name.split("_").pop();
            createGround(
              scene,
              boundingInfo.boundingBox,
              selectedAssetModel.type,
              groundMesh.position,
              groundMesh.name.split("_").pop() ?? "0",
            );
            handleAction({
              type: ActionCommandType.ADD_MODE,
              model: {
                id: meshId,
                type: selectedAssetModel.type,
                name,
                filename: selectedAssetModel.filename,
                rotation: selectedAssetModel.rotation,
                offset: selectedAssetModel.offset,
                scaling: selectedAssetModel.scaling,
                metadata: selectedAssetModel.metadata,
                position: {
                  x: groundMesh.position.x,
                  y: groundMesh.position.y,
                  z: groundMesh.position.z,
                },
              },
            });

            previewedMesh = null;
          } else {
            if (previewedMesh) {
              (previewedMesh.parent as TransformNode).dispose();
              previewedMesh = null;
            }
          }

          dispatch(actions.resetActionMenu());
          camera?.attachControl(scene.getEngine().getRenderingCanvas()!, true);
        } else if (selectedMesh) {
          // 既存アセットを移動
          const hitGroundMesh = scene.pick(scene.pointerX, scene.pointerY, (mesh) =>
            mesh.name.includes("ground"),
          );
          const outer = selectedMesh.parent as TransformNode;
          const boundingInfo = selectedMesh.getBoundingInfo();
          if (
            hitGroundMesh.hit &&
            hitGroundMesh.pickedMesh!.position.x === outer.position.x &&
            hitGroundMesh.pickedMesh!.position.z === outer.position.z &&
            canPlaceOnGround(hitGroundMesh.pickedMesh, selectedMesh.metadata.type)
          ) {
            const name = `asset_model_layer_${hitGroundMesh.pickedMesh!.position.x}_${hitGroundMesh.pickedMesh!.position.z}_${Math.round(boundingInfo.boundingBox.maximumWorld.y)}`;
            outer.getChildMeshes().forEach((subMesh) => {
              if (subMesh.material && !selectedMesh!.metadata?.rotation) {
                subMesh.material.alpha = 1;
              }
              if (subMesh.name !== "outer") {
                subMesh.name = name;
              }
            });
            selectedMesh.metadata["position"] = {
              x: hitGroundMesh.pickedMesh!.position.x,
              y: hitGroundMesh.pickedMesh!.position.y,
              z: hitGroundMesh.pickedMesh!.position.z,
            };
            selectedMesh.metadata["bottomLayer"] = hitGroundMesh.pickedMesh?.name.split("_").pop();
            createGround(
              scene,
              boundingInfo.boundingBox,
              selectedMesh.metadata.type,
              hitGroundMesh.pickedMesh!.position,
              hitGroundMesh.pickedMesh?.name.split("_").pop() ?? "0",
            );

            handleAction({
              type: ActionCommandType.CHANGE_MODE,
              id: selectedMesh.id,
              changes: {
                name,
                position: {
                  x: hitGroundMesh.pickedMesh!.position.x,
                  y: hitGroundMesh.pickedMesh!.position.y,
                  z: hitGroundMesh.pickedMesh!.position.z,
                },
              },
            });
          } else {
            const position = new Vector3(
              selectedMesh.metadata.position.x,
              selectedMesh.metadata.position.y,
              selectedMesh.metadata.position.z,
            );
            outer.position = position;
            outer.getChildMeshes().forEach((subMesh) => {
              if (subMesh.material && !selectedMesh!.metadata?.rotation) {
                subMesh.material.alpha = 1;
              }
            });
            createGround(
              scene,
              boundingInfo.boundingBox,
              selectedMesh.metadata.type,
              position,
              selectedMesh.metadata.bottomLayer,
            );
          }

          // すべての配置済みアセットを選択可能に
          setTarget(selectedMesh);

          if (!selectedMesh.metadata?.rotation) {
            camera?.attachControl(scene.getEngine().getRenderingCanvas()!, true);
          }

          selectedMesh = null;
        } else if (deleteMesh) {
          if (actionMenu.eraser) {
            // アセットを削除
            play();
            const ground = scene.getMeshByName(deleteMesh.name.replace("model", "ground"));
            if (deleteMesh.metadata?.type !== "ground" || ground) {
              const outer = deleteMesh.parent as TransformNode;
              const boundingInfo = deleteMesh.getBoundingInfo();
              createBottomGround(scene, boundingInfo.boundingBox, outer.position);
              handleAction({
                type: ActionCommandType.REMOVE_MODE,
                meshId: deleteMesh.id,
              });
              outer.dispose();
            }
          }
          deleteMesh = null;
          camera?.attachControl(scene.getEngine().getRenderingCanvas()!, true);
        } else {
          if (previewedMesh) {
            (previewedMesh.parent as TransformNode).dispose();
            previewedMesh = null;
          }
          camera?.attachControl(scene.getEngine().getRenderingCanvas()!, true);
        }

        pointerUpFlag = false;
      };

      // ポインターオブザーバー登録
      observable = scene.onPointerObservable.add(async (pointerInfo) => {
        const pointerId = (pointerInfo.event as IPointerEvent).pointerId;
        switch (pointerInfo.type) {
          case PointerEventTypes.POINTERDOWN:
            lastPointer = {
              x: (pointerInfo.event as IPointerEvent).clientX,
              y: (pointerInfo.event as IPointerEvent).clientY,
            };
            // 操作中に触れた2本目の指は無視する
            if (activePointerId !== null) return;
            activePointerId = pointerId;
            await pointerDown(pointerInfo.pickInfo!);
            break;
          case PointerEventTypes.POINTERMOVE:
            if (activePointerId !== null && pointerId !== activePointerId) return;
            await pointerMove();
            break;
          case PointerEventTypes.POINTERUP:
            if (activePointerId !== null && pointerId !== activePointerId) return;
            activePointerId = null;
            await pointerUp(pointerInfo.pickInfo!);
            break;
        }
      });

      // ジェスチャーが OS/ブラウザに奪われたときも掴んだままにしない
      handlePointerCancel = (event: PointerEvent) => {
        if (activePointerId !== null && event.pointerId !== activePointerId) return;
        abortInteraction();
      };
      canvas?.addEventListener("pointercancel", handlePointerCancel);
    }

    return () => {
      clicked = false;
      pointerUpFlag = false;
      pointerDownFlag = false;
      activePointerId = null;
      selectedMesh = null;
      previewedMesh = null;
      if (canvas && handlePointerCancel) {
        canvas.removeEventListener("pointercancel", handlePointerCancel);
      }
      if (observable && scene) {
        scene.onPointerObservable.remove(observable);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, actionMenu.eraser, selectedAssetModel, dispatch, handleAction, play]);
};
